import { deleteDoc, doc, type QueryConstraint, collection, query, getDocs, writeBatch, updateDoc, addDoc, Timestamp, where, setDoc, type Query, type UpdateData, getDoc } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { snapshotToArray } from "../../utils/fbUtil";

export type BaseRecord = {
  id: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  createdAtMs?: number;
  updatedAtMs?: number;
  [key: string]: unknown;
}

function normalizeBase(record: BaseRecord) {
  if (record.createdAt) {
    record.createdAtMs = record.createdAt.toMillis();
    delete record.createdAt;
  }
  if (record.updatedAt) {
    record.updatedAtMs = record.updatedAt.toMillis();
    delete record.updatedAt;
  }
}
function denormalizeBase(record: BaseRecord) {
  if (record.createdAtMs) {
    record.createdAt = Timestamp.fromMillis(record.createdAtMs);
    delete record.createdAtMs;
  }
  if (record.updatedAtMs) {
    record.updatedAt = Timestamp.fromMillis(record.updatedAtMs);
    delete record.updatedAtMs;
  }
}

export class BaseApi<T extends BaseRecord> {
  collectionName: string;
  defaultEntity: Record<string, unknown>;
  protected denormalize?: DenormalizeFn<T>;
  protected normalize?: NormalizeFn<T>;

  constructor(
    collectionName: string,
    defaultEntity: Record<string, unknown>,
    denormalize: DenormalizeFn<T> = denormalizeBase,
    normalize: NormalizeFn<T> = normalizeBase
  ) {
    this.collectionName = collectionName;
    // this.map = new Map();
    this.defaultEntity = defaultEntity;
    this.denormalize = denormalize;
    this.normalize = normalize;
  }

  async remove(id: string) {
    try {
      console.log("delete", this.collectionName, "/", id);

      await deleteDoc(doc(db, this.collectionName, id));
      return { result: { id } };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }

  async removeAll(w: QueryConstraint) {
    try {
      const ref = collection(db, this.collectionName);
      const q = query(ref, w);
      const s = await getDocs(q);
      // const ids = s.docs.map((docItem) => docItem.id);
      // console.log("delete all ", this.collectionName, "/", ids);
      // const p = ids.map(async (id) => await deleteDoc(doc(db, this.collectionName, id)));
      // await Promise.all(p);
      // return {result: ids}
      const MAX_WRITES_PER_BATCH = 500; /** https://cloud.google.com/firestore/quotas#writes_and_transactions */








      /**
       * `chunk` function splits the array into chunks up to the provided length.
       * You can get it from either:
       * - [Underscore.js](https://underscorejs.org/#chunk)
       * - [lodash](https://lodash.com/docs/4.17.15#chunk)
       * - Or one of [these answers](https://stackoverflow.com/questions/8495687/split-array-into-chunks#comment84212474_8495740)
       */
      const batches = chunk(s.docs, MAX_WRITES_PER_BATCH);
      console.log(
        `Removing ${s.docs.length} documents in ${batches.length} batches.`
      );
      const commitBatchPromises: Promise<void>[] = [];
      const deletedIds: string[] = [];

      batches.forEach((batchDocs) => {
        const batch = writeBatch(db);
        batchDocs.forEach((docItem) => {
          batch.delete(docItem.ref);
          deletedIds.push(docItem.id);
        });
        commitBatchPromises.push(batch.commit());
      });

      await Promise.all(commitBatchPromises);
      return { result: deletedIds };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }

  async update(id: string, changes: UpdateData<BaseRecord>) {
    try {
      console.log("update", this.collectionName, "/", id, changes);

      // check cache
      // if (!this.map.has(id)) {
      //   return { error: "id not in cache!" };
      // }
      const ref = doc(db, this.collectionName, id);
      await updateDoc(ref, changes);

      // update cache
      // const u = this.map.get(id);
      // Object.keys(changes).forEach((key) => (u[key] = changes[key]));
      // add log
      try {
        const logRef = collection(db, this.collectionName + "_log");
        const logDocRef = await addDoc(logRef, {
          action: "update",
          json: JSON.stringify(changes),
          itemId: id,
          timestamp: Timestamp.now(),
        });
        console.log("logDocRef:", logDocRef.id);
      } catch (error: unknown) {
        console.log(getErrorMessage(error));
      }

      return { result: { id } };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }
  async update2(
    before: T,
    changes: T
  ) {
    try {
      const id = before.id as string;

      const ref = doc(db, this.collectionName, id);
      const updatedAt = Timestamp.now();
      if (this.denormalize) {
        const doc = { ...changes };
        this.denormalize(doc);
        console.log("denormalize", changes, "=>", doc);
        await updateDoc(ref, { ...doc, updatedAt });
      } else {
        await updateDoc(ref, { ...changes, updatedAt });
      }

      // update cache
      // const u = this.map.get(id);
      // Object.keys(changes).forEach((key) => (u[key] = changes[key]));
      // add log
      try {
        const after = { ...before, ...changes };
        const text = JSON.stringify({before, after});
        const logRef = collection(db, this.collectionName + "_log");
        const logDocRef = await addDoc(logRef, {
          action: "update",
          patch: text,
          itemId: id,
          timestamp: Timestamp.now(),
        });
        console.log("logDocRef:", logDocRef.id);
      } catch (error: unknown) {
        console.log(getErrorMessage(error));
      }

      return { result: { ...before, ...changes } };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }

  async getLog(id: string) {
    try {
      const ref = collection(db, this.collectionName + "_log");
      const q = query(ref, where("itemId", "==", id));
      const querySnapshot = await getDocs(q);
      const result = snapshotToArray(querySnapshot);

      return { result };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }
  async updateLog(logId: string, changes: UpdateData<BaseRecord>) {
    try {
      const ref = doc(db, this.collectionName + "_log", logId);
      await updateDoc(ref, changes);
      return { result: true };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }
  /**
   *
   * @param {object} entity {id, ...}
   * @returns
   */
  async save(entity: Record<string, unknown> & { id?: string | null; }) {
    return this.saveOrCreate(entity);
  }

  async saveOrCreate(entity: Record<string, unknown> & { id?: string | null; }) {
    try {
      const id = entity.id;
      if (!id) {
        return { error: "missing id!" };
      }

      const body: Record<string, unknown> = {};
      Object.keys(this.defaultEntity).forEach((key) => {
        body[key] = entity[key] ?? this.defaultEntity[key];
      });

      const ref = doc(db, this.collectionName, id);
      await setDoc(ref, body);

      // save to cache
      body.id = id;
      // this.map.set(id, cloneObj(body));
      return { result: body };
    } catch (ex: unknown) {
      console.log(getErrorMessage(ex));
      return { error: getErrorMessage(ex) };
    }
  }

  // async save(entity) {
  //   try {
  //     const id = entity.id;
  //     if (!id) {
  //       return { error: "missing id!" };
  //     }
  //     const old = this.map.get(id);
  //     const changes = {};
  //     Object.keys(this.defaultEntity).forEach((key) => {
  //       if (entity[key] !== old[key]) {
  //         changes[key] = entity[key];
  //       }
  //     });
  //     if (Object.keys(changes).length > 0) {
  //       return await this.update(id, changes);
  //     } else {
  //       console.log("no change");
  //       return { result: cloneObj(old) };
  //     }
  //   } catch (ex) {
  //     console.log(ex.message);
  //     return { error: ex.message };
  //   }
  // }
  /**
   * entity not has property id
   * @param {} entity
   * @returns
   */
  async create({
    id = null, ...entity
  }: Record<string, unknown> & { id?: string | null; }) {
    try {
      const body: Record<string, unknown> = {};
      Object.keys(this.defaultEntity).forEach((key) => {
        body[key] = entity[key] ?? this.defaultEntity[key];
      });

      if (id) {
        const ref = doc(db, this.collectionName, id);
        await setDoc(ref, body);
        body.id = id;
        // this.map.set(id, cloneObj(body));
      } else {
        const ref = collection(db, this.collectionName);
        const docRef = await addDoc(ref, body);
        console.log(docRef.id);
        // save to cache
        body.id = docRef.id;
        // this.map.set(docRef.id, cloneObj(body));
      }

      // log
      try {
        const logRef = collection(db, this.collectionName + "_log");
        const logDocRef = await addDoc(logRef, {
          action: "create",
          json: JSON.stringify(body),
          itemId: body.id,
          timestamp: Timestamp.now(),
        });
        console.log("logDocRef:", logDocRef.id);
      } catch (error: unknown) {
        console.log(getErrorMessage(error));
      }

      return { result: body };
    } catch (ex: unknown) {
      console.log(getErrorMessage(ex));
      return { error: getErrorMessage(ex) };
    }
  }
  /**
   * entity not has property id
   * @param {} entity
   * @returns
   */
  async createMany(items: T[]) {
    try {
      const batch = writeBatch(db);
      const colRef = collection(db, this.collectionName);
      const createdIds: string[] = [];

      items.forEach((item) => {
        const ref = item.id ? doc(colRef, String(item.id)) : doc(colRef);
        createdIds.push(ref.id);

        if (this.denormalize) {
          const data = { ...item };
          this.denormalize(data);
          batch.set(ref, data);
        } else {
          batch.set(ref, item);
        }
      });

      await batch.commit();
      console.log(createdIds);

      items.forEach((t, idx) => (t.id = createdIds[idx]));

      // log
      try {
        const logBatch = writeBatch(db);
        const logRef = collection(db, this.collectionName + "_log");
        const logCreatedIds: string[] = [];
        const logObjs = items.map((item) => {
          const l = {
            action: "create",
            json: JSON.stringify(item),
            itemId: item.id,
            timestamp: Timestamp.now(),
          };
          return l;
        });
        logObjs.forEach((l) => {
          const logDocRef = doc(logRef);
          logCreatedIds.push(logDocRef.id);
          logBatch.set(logDocRef, l);
        });

        await logBatch.commit();
        // console.log(logCreatedIds);
      } catch (error: unknown) {
        console.log(getErrorMessage(error));
      }

      return { result: items };
    } catch (ex: unknown) {
      console.log(getErrorMessage(ex));
      return { error: getErrorMessage(ex) };
    }
  }
  async query(w: QueryConstraint) {
    try {
      const ref = collection(db, this.collectionName);
      const q = query(ref, w);
      const querySnapshot = await getDocs(q);
      const result = snapshotToArray(querySnapshot);

      return { result };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }

  async query2(q: Query<unknown>) {
    try {
      const querySnapshot = await getDocs(q);
      const result = snapshotToArray(querySnapshot);

      // result.forEach((u) => this.map.set(u.id, cloneObj(u)));
      return { result };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }

  async getAll() {
    try {
      const querySnapshot = await getDocs(collection(db, this.collectionName));
      const result = snapshotToArray(querySnapshot);

      return { result };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }

  async getOne(id: string) {
    const ref = doc(db, this.collectionName, id);
    const docSnap = await getDoc(ref);
    if (docSnap.exists()) {
      const obj = { ...docSnap.data(), id: docSnap.id };
      // this.map.set(obj.id, cloneObj(obj));
      return { result: obj };
    } else {
      return { error: "not found" };
    }
  }
}

/**
 * CURD
 */

export type DenormalizeFn<T> = (doc: T) => void;
export type NormalizeFn<T> = (doc: T) => void;
export function getErrorMessage(ex: unknown): string {
  return ex instanceof Error ? ex.message : String(ex);
}
// import latinize from "latinize";
/**
 * Splits an array into chunks of a specified size.
 * @param {Array} arr
 * @param {number} size
 * @returns {Array[]}
 */
export function chunk<T>(arr: T[], size: number): T[][] {
  const res: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    res.push(arr.slice(i, i + size));
  }
  return res;
}

